import {
  Anchor,
  Badge,
  Button,
  Checkbox,
  Container,
  createTheme,
  CSSVariablesResolver,
  Divider,
  Drawer,
  Flex,
  Group,
  Paper,
  Radio,
  rem,
  Skeleton,
  Stack,
  Stepper,
  TextInput,
  Title,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { Notifications } from "@mantine/notifications";
import { IconCheck } from "@tabler/icons-react";
const CONTAINER_SIZES: Record<string, string> = {
  xxs: rem(300),
  xs: rem(400),
  sm: rem(500),
  md: rem(600),
  lg: rem(700),
  xl: rem(1100),
  xxl: rem(1344),
};

export const THEME = {
  defaultRadius: "lg",
};

export const theme = createTheme({
  primaryColor: "green",
  colors: {
    green: [
      "#e2fff5",
      "#cdffeb",
      "#8CFFD0",
      "#67ffc0",
      "#3fffae",
      "#27ffa2",
      "#17ff9c",
      "#00e386",
      "#00c976",
      "#00ae63",
    ],
    gray: [
      "#fafafa",
      "#e5e6e7",
      "#c7cacf",
      "#a7adb8",
      "#8c94a4",
      "#7a8499",
      "#717c94",
      "#5f6a81",
      "#545e74",
      "#455168",
    ],
    white: [
      "#f5f5f5",
      "#e7e7e7",
      "#cdcdcd",
      "#b2b2b2",
      "#9a9a9a",
      "#8b8b8b",
      "#848484",
      "#717171",
      "#656565",
      "#575757",
    ],
    violet: [
      "#f0eaff",
      "#dbcfff",
      "#b39cff",
      "#8C6AFE",
      "#6537fd",
      "#4f1bfc",
      "#430bfd",
      "#3401e2",
      "#2c00cb",
      "#2200b3",
    ],
    error: [
      "#ffe9e9",
      "#ffd4d4",
      "#f4a8a8",
      "#ec7879",
      "#e55050",
      "#e13636",
      "#e02728",
      "#c61a1d",
      "#b21217",
      "#9c0412",
    ],
    layout: [
      "#8CFFD0", // layout btn
      "#DBFFF2", // aquamarine card
      "#8C6AFE", // layout link
      "#E7E5EA", // gray card
      "#3B3B40", // layout black text
      "#F5F4F7", // quote background
      "#878787", // quote additional text
      "#292C33", // section header
      "#D6D7E7", // light gray card
      "#777876", // footer additional text
      "#767678", // project card description
    ],
  },

  fontFamily: "Inter",
  defaultRadius: "xl",

  components: {
    Paper: Paper.extend({
      defaultProps: {
        radius: "xl",
        shadow: "none",
        bg: "gray.0",
      },
      styles: {
        root: {
          padding: "var(--mantine-spacing-lg)",
        },
      },
    }),

    TextInput: TextInput.extend({
      defaultProps: {
        variant: "filled",
      },
    }),
    DatePickerInput: DatePickerInput.extend({
      styles: {
        label: {
          paddingBottom: "0.5em",
        },
      },
    }),
    Skeleton: Skeleton.extend({
      defaultProps: {
        radius: "lg",
      },
    }),
    Stack: Stack.extend({
      defaultProps: {
        gap: "lg",
      },
    }),
    Group: Group.extend({
      defaultProps: {
        gap: "lg",
      },
    }),
    Checkbox: Checkbox.extend({
      defaultProps: {
        iconColor: "white.9",
      },
      styles: {
        root: {
          cursor: "pointer",
        },
        body: {
          alignItems: "center",
        },
      },
    }),
    Stepper: Stepper.extend({
      defaultProps: {
        completedIcon: <IconCheck color={"black"} />,
      },
    }),
    Radio: Radio.extend({
      defaultProps: {
        iconColor: "white.9",
      },
      styles: {
        body: {
          alignItems: "center",
        },
      },
    }),
    Flex: Flex.extend({
      defaultProps: {
        gap: "lg",
      },
    }),
    Drawer: Drawer.extend({
      defaultProps: {
        position: "right",
      },
      styles: {
        content: {
          padding: 0,
        },
      },
    }),
    Badge: Badge.extend({
      defaultProps: {
        c: "white",
      },
    }),

    Title: Title.extend({
      defaultProps: {
        fw: 700,
      },
    }),
    Container: Container.extend({
      vars: (_, { size, fluid }) => ({
        root: {
          "--container-size": fluid
            ? "100%"
            : size !== undefined && size in CONTAINER_SIZES
            ? CONTAINER_SIZES[size]
            : rem(size),
        },
      }),
    }),
    Button: Button.extend({
      defaultProps: {
        radius: "xl",
        variant: "filled",
        color: "green.3",
      },
    }),
    Anchor: Anchor.extend({
      defaultProps: {
        c: "violet.3",
      },
    }),
    Divider: Divider.extend({
      defaultProps: {
        color: "gray.1",
      },
    }),
  },

  other: {
    beige: "#EBDFD5",
    brown: "#42221A",
    beigeLight: "#DAD0A7",
    typography: {
      success: "#1E5945",
    },
    margin: {
      main: "2rem",
      min: "1rem",
    },
  },
});

export const resolver: CSSVariablesResolver = (theme) => ({
  variables: {
    "--mantine-color-beige": theme.other.beige,
    "--mantine-color-beige-light": theme.other.beigeLight,
    "--mantine-color-brown": theme.other.brown,
    "--mantine-margin-element": "1.5rem",
    "--mantine-margin-main": theme.other.margin.main,
    "--mantine-margin-min": theme.other.margin.min,
    "--mantine-header-height": "4rem",
    "--mantine-footer-height": "3.5rem",
  },
  light: {},
  dark: {},
});

export const mobileTheme = createTheme({
  components: {
    ...theme.components,
    Notifications: Notifications.extend({
      defaultProps: {
        position: "top-center",
      },
    }),
  },
});
